class CartDrawer extends HTMLElement {
  static shippingFee = 1500;

  static addOnHandles = [
    'soft-cotton-tummy-control-breathable-for-daily-wear-cotton',
    'tummy-control-butt-lifting-high-waist-seamless-lace-briefs',
    'sexy-open-crotch-buttockless-design-hollow-out-lace-thong',
  ];

  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
    this.updateShippingExperience();
    document.addEventListener('vk:cart-shipping-updated', (event) => {
      if (this.classList.contains('active')) this.updateShippingExperience(event.detail);
    });
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay || overlay.dataset.cartDrawerBound === 'true') return;

    overlay.dataset.cartDrawerBound = 'true';
    overlay.addEventListener('click', this.close.bind(this));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.openFromHeader(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.openFromHeader(cartLink);
      }
    });
  }

  async openFromHeader(cartLink) {
    if (this.isRefreshing) return;

    this.isRefreshing = true;
    cartLink.setAttribute('aria-busy', 'true');

    try {
      await this.refreshContents();
      this.open(cartLink);
    } catch (error) {
      console.error('Unable to refresh the cart drawer.', error);
      window.location.href = routes.cart_url;
    } finally {
      this.isRefreshing = false;
      cartLink.removeAttribute('aria-busy');
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.updateShippingExperience();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerSection = parsedState?.sections?.['cart-drawer'];
    const sourceDrawer = this.getSectionDOM(drawerSection, 'cart-drawer');

    if (!sourceDrawer) {
      this.refreshContents()
        .then(() => this.open())
        .catch(() => {
          window.location.href = routes.cart_url;
        });
      return;
    }

    this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const sectionContents = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (sectionContents !== null) sectionElement.innerHTML = sectionContents;
    });

    setTimeout(() => {
      this.bindOverlay();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (typeof html !== 'string') return null;

    const section = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return section ? section.innerHTML : null;
  }

  async refreshContents() {
    const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`, {
      headers: { Accept: 'text/html' },
    });
    if (!response.ok) throw new Error('Cart drawer section request failed');

    const responseText = await response.text();
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    const sourceDrawer = html.querySelector('cart-drawer');
    const sourceInner = sourceDrawer?.querySelector('.drawer__inner');
    const targetInner = this.querySelector('.drawer__inner');

    if (!sourceDrawer || !sourceInner || !targetInner) {
      throw new Error('Cart drawer section is missing');
    }

    targetInner.replaceWith(sourceInner);
    this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
    this.bindOverlay();
    this.updateShippingExperience();
  }

  formatMoney(cents, currency = 'USD') {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  async updateShippingExperience(state) {
    const progress = this.querySelector('.lv-cart-drawer-shipping');
    const items = this.querySelector('cart-drawer-items');
    if (!progress || !items || this.classList.contains('is-empty')) return;

    const shipping = window.VeraKissCartShipping;
    if (!shipping) return;
    const request = this.shippingRequest = (this.shippingRequest || 0) + 1;
    try {
      const cart = state || await shipping.read();
      if (request !== this.shippingRequest || !shipping.isCurrent(cart)) return;
      shipping.render(progress, cart);
      const remaining = cart.remaining;
      const currency = cart.currency;

      if (!cart.canSuggest) {
        this.querySelector('[data-shipping-addons]')?.remove();
        return;
      }

      const products = await this.loadShippingAddOns();
      if (request !== this.shippingRequest || !shipping.isCurrent(cart) || !progress.isConnected) return;
      const cartHandles = new Set(cart.items.map((item) => item.handle));
      const candidates = products
        .map((product) => {
          const prices = product.variants.filter((variant) => variant.available).map((variant) => variant.price);
          return { product, price: prices.length ? Math.min(...prices) : 0 };
        })
        .filter(({ product, price }) => !cartHandles.has(product.handle) && price >= remaining && price <= CartDrawer.shippingFee)
        .slice(0, 3);

      this.renderShippingAddOns(items, candidates, remaining, currency);
    } catch (error) {
      this.querySelector('[data-shipping-addons]')?.remove();
      if (request === this.shippingRequest && progress.isConnected) shipping.render(progress, { mode: 'pending' });
      console.error('Unable to update the free-shipping helper.', error);
    }
  }

  loadShippingAddOns() {
    if (!this.shippingAddOnsPromise) {
      const root = window.Shopify?.routes?.root || '/';
      this.shippingAddOnsPromise = Promise.all(
        CartDrawer.addOnHandles.map((handle) =>
          fetch(`${root}products/${handle}.js`, { headers: { Accept: 'application/json' } }).then((response) => {
            if (!response.ok) throw new Error('Add-on product request failed');
            return response.json();
          })
        )
      );
    }
    return this.shippingAddOnsPromise;
  }

  renderShippingAddOns(items, candidates, remaining, currency) {
    this.querySelector('[data-shipping-addons]')?.remove();
    if (!candidates.length) return;

    const section = document.createElement('section');
    const collectionUrl = `${window.Shopify?.routes?.root || '/'}collections/free-shipping-add-ons`;
    section.className = 'lv-shipping-addons';
    section.dataset.shippingAddons = '';
    section.setAttribute('aria-labelledby', 'CartDrawer-ShippingAddOnsTitle');

    const rows = candidates
      .map(({ product, price }) => {
        const image = product.featured_image
          ? `<img src="${this.escapeHTML(product.featured_image)}" alt="" width="64" height="80" loading="lazy">`
          : '';
        const url = product.url || `/products/${product.handle}`;
        return `<li class="lv-shipping-addons__item">
          <a href="${this.escapeHTML(url)}" class="lv-shipping-addons__link">
            <span class="lv-shipping-addons__media">${image}</span>
            <span class="lv-shipping-addons__details">
              <span class="lv-shipping-addons__name">${this.escapeHTML(product.title)}</span>
              <span class="lv-shipping-addons__price">From ${this.formatMoney(price, currency)}</span>
            </span>
            <span class="lv-shipping-addons__action">Choose size</span>
          </a>
        </li>`;
      })
      .join('');

    section.innerHTML = `<div class="lv-shipping-addons__intro">
        <h3 id="CartDrawer-ShippingAddOnsTitle">Add a little. Save the $15 shipping.</h3>
        <p>Choose an add-on from ${this.formatMoney(remaining, currency)} to reach free shipping for less.</p>
      </div>
      <ul class="lv-shipping-addons__list" role="list">${rows}</ul>
      <p class="lv-shipping-addons__note">Briefs are final sale. Select your size on the product page.</p>
      <a href="${this.escapeHTML(collectionUrl)}" class="lv-shipping-addons__browse">Browse all free-shipping add-ons</a>`;

    items.append(section);
  }

  escapeHTML(value) {
    const element = document.createElement('span');
    element.textContent = String(value || '');
    return element.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
